import { Plus, Trash2 } from "lucide-react";
import {
  MEAL_METRICS,
  type MealCondition,
  type MealLogConfig,
  type MealMetric,
} from "@/lib/schema";
import type { WidgetSettingsEditorProps } from "@/widgets/types";
import { METRIC_LABELS, metricUnit } from "./metrics";
import { maxScoreDay, maxScoreWeek } from "./scoring";

type Props = WidgetSettingsEditorProps<MealLogConfig>;

export function SettingsEditor({
  config,
  onChange,
  errors = {},
  scoringField,
}: Props) {
  const update = (patch: Partial<MealLogConfig>) => onChange(patch);
  const fieldError = (key: string) =>
    errors[key] ? (
      <p className="field-error" data-testid={`widget-error-${key}`}>
        {errors[key]}
      </p>
    ) : null;

  const updateCondition = (id: string, patch: Partial<MealCondition>) => {
    update({
      conditions: config.conditions.map((c) =>
        c.id === id ? { ...c, ...patch } : c,
      ),
    });
  };

  const addCondition = () => {
    const next: MealCondition = {
      id: crypto.randomUUID(),
      metric: "kcal",
      direction: "atMost",
      threshold: Number.NaN,
      points: 1,
    };
    update({ conditions: [...config.conditions, next] });
  };

  const removeCondition = (id: string) => {
    const next = config.conditions.filter((c) => c.id !== id);
    if (next.length === 0) {
      update({ conditions: next, weekGoal: undefined });
    } else {
      update({ conditions: next });
    }
  };

  const numberOrEmpty = (value: number): number | "" =>
    Number.isFinite(value) ? value : "";

  return (
    <div
      className="widget-settings"
      data-testid={`widget-settings-${config.id}`}
    >
      <div className="setting-row">
        <label className="setting-label" htmlFor={`label-${config.id}`}>
          Tittel
        </label>
        <div className="setting-control">
          <input
            id={`label-${config.id}`}
            className="input"
            type="text"
            value={config.label}
            onChange={(e) => update({ label: e.target.value })}
            data-testid="widget-label-input"
          />
          {fieldError("label")}
        </div>
      </div>

      <div className="task-list">
        <div className="section-head">
          <h3>Betingelser</h3>
          <span className="hint">
            Poeng per betingelse som er oppfylt når dagen er ferdig. Uten
            betingelser er widgeten kun sporing. Rekkefølgen ordnes ved lagring
            — metrikken med flest poeng først, mildeste betingelse først.
          </span>
        </div>
        {fieldError("conditions")}
        {fieldError("conditionPoints")}
        {config.conditions.map((condition) => (
          <div
            key={condition.id}
            className="task-row condition-row"
            data-testid={`condition-row-${condition.id}`}
          >
            <select
              className="input"
              value={condition.metric}
              onChange={(e) =>
                updateCondition(condition.id, {
                  metric: e.target.value as MealMetric,
                })
              }
              data-testid={`condition-metric-select-${condition.id}`}
            >
              {MEAL_METRICS.map((metric) => (
                <option key={metric} value={metric}>
                  {METRIC_LABELS[metric]}
                </option>
              ))}
            </select>
            <select
              className="input"
              value={condition.direction}
              onChange={(e) =>
                updateCondition(condition.id, {
                  direction: e.target.value as MealCondition["direction"],
                })
              }
              data-testid={`condition-direction-select-${condition.id}`}
            >
              <option value="atMost">Høyst</option>
              <option value="atLeast">Minst</option>
            </select>
            <div className="num-input">
              <input
                type="number"
                min={0}
                step="any"
                value={numberOrEmpty(condition.threshold)}
                onChange={(e) => {
                  const v = e.target.value;
                  updateCondition(condition.id, {
                    threshold:
                      v === "" ? Number.NaN : Math.max(0, Number(v) || 0),
                  });
                }}
                data-testid={`condition-threshold-input-${condition.id}`}
              />
              <span className="unit">{metricUnit(condition.metric)}</span>
            </div>
            <div className="num-input">
              <input
                type="number"
                min={0.25}
                step={0.25}
                value={numberOrEmpty(condition.points)}
                onChange={(e) => {
                  const v = e.target.value;
                  updateCondition(condition.id, {
                    points: v === "" ? Number.NaN : Math.max(0, Number(v) || 0),
                  });
                }}
                data-testid={`condition-points-input-${condition.id}`}
              />
              <span className="unit">p</span>
            </div>
            <button
              type="button"
              className="icon-btn-delete"
              onClick={() => removeCondition(condition.id)}
              aria-label="Slett betingelse"
              data-testid={`delete-condition-button-${condition.id}`}
            >
              <Trash2 size={16} />
            </button>
          </div>
        ))}
        <button
          type="button"
          className="btn ghost icon-btn"
          onClick={addCondition}
          data-testid="add-condition-button"
        >
          <Plus size={14} />
          Legg til betingelse
        </button>
      </div>

      {config.conditions.length > 0 ? (
        <>
          {scoringField}
          <Summary config={config} />
        </>
      ) : null}
    </div>
  );
}

function Summary({ config }: { config: MealLogConfig }) {
  const maxDay = maxScoreDay(config);
  const max = maxScoreWeek(config);
  const goal = config.weekGoal;
  const goalSet = goal !== undefined && Number.isFinite(goal);
  const goalPct =
    !goalSet || max === 0 ? null : Math.round(((goal as number) / max) * 100);
  return (
    <div className="summary" data-testid={`widget-summary-${config.id}`}>
      <h4>Sammendrag</h4>
      <p>
        Maks poeng per dag:{" "}
        <strong data-testid="summary-max-day">{maxDay}p</strong>
      </p>
      <p>
        Maks poeng denne uka: <strong data-testid="summary-max">{max}p</strong>
      </p>
      <p>
        Mål:{" "}
        <strong data-testid="summary-goal">
          {!goalSet
            ? "—"
            : goalPct === null
              ? `${goal}p`
              : `${goal}p (${goalPct}%)`}
        </strong>
      </p>
    </div>
  );
}
